import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../lib/ctx";

const left = (iso) => Math.max(0, Math.floor((new Date(iso) - Date.now()) / 1000));
const pad = (n) => String(n).padStart(2, "0");

const Box = ({ v, label, testid }) => (
  <span className="flex flex-col items-center">
    <span className="min-w-[2.6rem] rounded bg-[#222] px-2 py-1 text-center font-mono text-lg font-bold text-white sm:text-2xl" data-testid={testid}>{pad(v)}</span>
    <span className="mt-1 text-[9px] font-bold uppercase tracking-widest text-white/80">{label}</span>
  </span>
);

export const usePromo = () => {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/promotions/active").then((r) => { setD(r.data); }).catch(() => {});
  }, [tick]);
  return [d, () => setTick((x) => x + 1)];
};

export const PromoBanner = () => {
  const { lang } = useLang();
  const [d, reload] = usePromo();
  const promo = d?.active || d?.next;
  const live = !!d?.active;
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!promo) { return undefined; }
    const target = live ? promo.end_at : promo.start_at;
    setS(left(target));
    const id = setInterval(() => {
      const v = left(target);
      setS(v);
      if (v === 0) { clearInterval(id); setTimeout(reload, 1500); }
    }, 1000);
    return () => { clearInterval(id); };
  }, [promo, live]);
  if (!promo) return null;
  const pcts = [promo.all_pct || 0, ...Object.values(promo.discounts || {})];
  const max = Math.max(...pcts);
  const bm = lang === "bm";
  return (
    <section className="mx-auto mt-4 max-w-7xl px-0 sm:px-8 lg:px-12" data-testid="promo-banner">
      <div className="keep-dark relative overflow-hidden rounded-sm bg-gradient-to-r from-[#d0011b] via-[#ee4d2d] to-[#ff7337] p-5 sm:p-7">
        <span className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
        <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/90"><Zap className="h-4 w-4 fill-current" />{live ? (bm ? "Sedang berlangsung" : "Live now") : (bm ? "Akan datang" : "Coming soon")}</p>
            <h2 className="mt-1 text-3xl font-extrabold text-white sm:text-4xl" data-testid="promo-name">{promo.name}</h2>
            <p className="mt-1 text-sm text-white/90" data-testid="promo-text">{promo.banner_text || (bm ? `Diskaun sehingga ${max}%` : `Up to ${max}% off`)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-start gap-1.5" data-testid="promo-countdown">
              <span className="mr-1 self-center text-[11px] font-bold uppercase text-white/90">{live ? (bm ? "Tamat dalam" : "Ends in") : (bm ? "Bermula dalam" : "Starts in")}</span>
              <Box v={Math.floor(s / 86400)} label={bm ? "Hari" : "Days"} testid="promo-days" />
              <Box v={Math.floor((s % 86400) / 3600)} label={bm ? "Jam" : "Hrs"} testid="promo-hours" />
              <Box v={Math.floor((s % 3600) / 60)} label={bm ? "Min" : "Min"} testid="promo-mins" />
              <Box v={s % 60} label={bm ? "Saat" : "Sec"} testid="promo-secs" />
            </div>
            {live && <Link to="/special-offers" className="rounded-sm bg-white px-5 py-3 text-sm font-bold text-[#ee4d2d] transition-transform duration-200 hover:-translate-y-0.5" data-testid="promo-shop-btn">{bm ? "Beli Sekarang" : "Shop the Sale"}</Link>}
          </div>
        </div>
      </div>
    </section>
  );
};
